// Live doc / collection hooks over the data plane (Firestore compat SDK or
// the pg facade), shared by the Dev Console and the dock's dev-session
// bridge (components/AgentDock.js) so both follow the same records.
import { useEffect, useState } from 'react';
import { db } from '../firebaseConfig';
import { isPg, subscribeRows, subscribeRow } from '../data/supabaseClient';
import { resolvePath } from '../data/pathMap';

export function useDoc(path) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    if (!path) { setDoc(null); return undefined; }
    if (isPg()) {
      const { table, id } = resolvePath(path);
      return subscribeRow(table, id, setDoc, () => setDoc(null));
    }
    return db.doc(path).onSnapshot(
      (s) => setDoc(s.exists ? { id: s.id, ...s.data() } : null),
      () => setDoc(null)
    );
  }, [path]);
  return doc;
}

export function useOrdered(path, field, dir = 'asc', limit = 500) {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    if (!path) { setDocs([]); return undefined; }
    if (isPg()) {
      // devSessions / devSessions/{id}/events / devJobs/{id}/logs ride
      // postgres_changes on their doc-tables; order (e.g. data->>'seq') and
      // limit are applied client-side by subscribeRows.
      const { table, filters } = resolvePath(path);
      return subscribeRows(table, filters, { orderBy: field, dir, limit }, setDocs, () => setDocs([]));
    }
    return db.collection(path).orderBy(field, dir).limit(limit).onSnapshot(
      (s) => setDocs(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setDocs([])
    );
  }, [path, field, dir, limit]);
  return docs;
}

// Which dev session the console has open — the dock follows it by voice.
// In-memory (the console keeps the selection in state, not the URL).
let activeId = null;
const listeners = new Set();
export function setActiveDevSession(id) {
  const next = id || null;
  if (next === activeId) return;
  activeId = next;
  for (const fn of listeners) { try { fn(activeId); } catch (_) { /* listener */ } }
}
export function getActiveDevSession() { return activeId; }
export function useActiveDevSession() {
  const [id, setId] = useState(activeId);
  useEffect(() => { listeners.add(setId); return () => { listeners.delete(setId); }; }, []);
  return id;
}
